import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, ArrowLeft, Plus, Pencil, Trash2,
  MessageCircle, Mail, Check, Undo2, MailOpen, Sparkles, Copy,
} from "lucide-react";
import { CONTACT } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_POST = { title: "", tag: "", date: "", excerpt: "", body: "", closing: "", published: true };

const inputCls =
  "w-full rounded-xl border border-pawc-teal/20 bg-white px-4 py-3 text-sm text-pawc-ink placeholder:text-pawc-slate/50 focus:outline-none focus:ring-2 focus:ring-pawc-teal/40";

const waNumber = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return digits.startsWith("0") ? "27" + digits.slice(1) : digits;
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("pawc_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState(null);
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyLoading, setReplyLoading] = useState({});
  const [aiTheme, setAiTheme] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const streamInto = async (path, payload, onChunk) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok || !res.body) throw new Error("stream failed");
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (data === "[DONE]") return;
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) {
            toast.error(parsed.error);
            return;
          }
          if (parsed.text) onChunk(parsed.text);
        } catch {
          /* ignore partial chunks */
        }
      }
    }
  };

  const draftReply = async (q) => {
    setReplyLoading((m) => ({ ...m, [q.id]: true }));
    setReplyDrafts((m) => ({ ...m, [q.id]: "" }));
    try {
      await streamInto("/admin/ai/reply-draft", { enquiry_id: q.id }, (t) =>
        setReplyDrafts((m) => ({ ...m, [q.id]: (m[q.id] || "") + t }))
      );
    } catch {
      toast.error("AI drafting failed. Please try again.");
    } finally {
      setReplyLoading((m) => ({ ...m, [q.id]: false }));
    }
  };

  const draftEssay = async () => {
    if (!aiTheme.trim()) {
      toast.error("Give Claude a theme to write from first.");
      return;
    }
    setAiBusy(true);
    setDraft((d) => ({ ...d, body: "" }));
    try {
      await streamInto("/admin/ai/essay-draft", { theme: aiTheme.trim() }, (t) =>
        setDraft((d) => ({ ...d, body: d.body + t }))
      );
    } catch {
      toast.error("AI drafting failed. Please try again.");
    } finally {
      setAiBusy(false);
    }
  };

  const authHeaders = (t = token) => ({ headers: { Authorization: `Bearer ${t}` } });

  const handleExpired = () => {
    sessionStorage.removeItem("pawc_admin_token");
    setToken("");
    setEnquiries(null);
    setPosts(null);
  };

  const fetchEnquiries = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, authHeaders(t));
      setEnquiries(data);
    } catch (e) {
      if (e.response?.status === 401) handleExpired();
    }
  };

  const fetchPosts = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/reflections`, authHeaders(t));
      setPosts(data);
    } catch (e) {
      if (e.response?.status === 401) handleExpired();
    }
  };

  useEffect(() => {
    if (token) {
      fetchEnquiries(token);
      fetchPosts(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("pawc_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("pawc_admin_token");
    setToken("");
    setEnquiries(null);
    setPosts(null);
    setPassword("");
  };

  const toggleAnswered = async (q) => {
    try {
      await axios.patch(
        `${API}/admin/enquiries/${q.id}`,
        { answered: !q.answered },
        authHeaders()
      );
      setEnquiries((list) =>
        list.map((item) => (item.id === q.id ? { ...item, answered: !q.answered } : item))
      );
    } catch {
      toast.error("Could not update the enquiry. Please try again.");
    }
  };

  const sendDigest = async () => {
    try {
      await axios.post(`${API}/admin/digest`, {}, authHeaders());
      toast.success("Digest sent to your inbox.");
    } catch {
      toast.error("Could not send the digest. Please try again.");
    }
  };

  const deleteEnquiry = async (q) => {
    if (!window.confirm(`Delete the enquiry from ${q.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${q.id}`, authHeaders());
      setEnquiries((list) => list.filter((item) => item.id !== q.id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete the enquiry. Please try again.");
    }
  };

  const openEditor = (post) => {
    if (post) {
      setDraft({
        title: post.title,
        tag: post.tag,
        date: post.date,
        excerpt: post.excerpt,
        body: (post.body || []).join("\n\n"),
        closing: post.closing || "",
        published: post.published,
      });
      setEditing(post.id);
    } else {
      setDraft(EMPTY_POST);
      setEditing("new");
    }
  };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/reflections`, draft, authHeaders());
        toast.success("Reflection saved.");
      } else {
        await axios.put(`${API}/admin/reflections/${editing}`, draft, authHeaders());
        toast.success("Reflection updated.");
      }
      setEditing(null);
      setDraft(EMPTY_POST);
      fetchPosts();
    } catch {
      toast.error("Could not save. Please check the fields and try again.");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    await axios.delete(`${API}/admin/reflections/${post.id}`, authHeaders());
    fetchPosts();
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-pawc-ink flex items-center justify-center px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl bg-pawc-sand p-10 shadow-2xl"
        >
          <span className="inline-flex w-14 h-14 rounded-2xl bg-pawc-mist items-center justify-center mb-6 p-1.5">
            <img src="/images/logo-mark.png" alt="Yielding logo" className="w-full h-full object-contain" />
          </span>
          <h1 className="font-serif text-3xl text-pawc-deep mb-2">Practice login</h1>
          <p className="text-sm text-pawc-slate mb-8">
            Enquiries dashboard for {CONTACT.brand}: {CONTACT.brandSub}.
          </p>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            aria-label="Admin password"
            className="w-full rounded-xl border border-pawc-teal/20 bg-white px-4 py-3.5 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-pawc-teal/40"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-700 mb-4">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-pawc-terra hover:bg-pawc-terraDark disabled:opacity-60 text-white py-3.5 text-sm transition-colors duration-300"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <a
            data-testid="admin-back-link"
            href="/"
            className="mt-6 inline-flex items-center gap-2 text-xs text-pawc-slate hover:text-pawc-teal transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Back to website
          </a>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-pawc-sand">
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl text-pawc-deep">Practice dashboard</h1>
            <p className="text-sm text-pawc-slate mt-1">Enquiries and Slow Water reflections.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="admin-digest-btn"
              onClick={sendDigest}
              className="inline-flex items-center gap-2 rounded-full border border-pawc-teal/30 text-pawc-teal px-5 py-2.5 text-sm hover:bg-pawc-mist transition-colors duration-300"
            >
              <MailOpen size={15} /> Send digest now
            </button>
            <button
              data-testid="admin-refresh-btn"
              onClick={() => { fetchEnquiries(); fetchPosts(); }}
              className="inline-flex items-center gap-2 rounded-full border border-pawc-teal/30 text-pawc-teal px-5 py-2.5 text-sm hover:bg-pawc-mist transition-colors duration-300"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-pawc-ink text-pawc-sand px-5 py-2.5 text-sm hover:bg-pawc-deep transition-colors duration-300"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-10" role="tablist">
          {[
            { id: "enquiries", label: "Enquiries" },
            { id: "reflections", label: "Slow Water posts" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => { setTab(t.id); setEditing(null); }}
              className={`rounded-full px-6 py-2.5 text-sm transition-colors duration-300 ${
                tab === t.id
                  ? "bg-pawc-teal text-pawc-sand"
                  : "border border-pawc-teal/30 text-pawc-teal hover:bg-pawc-mist"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          enquiries === null ? (
            <p className="text-pawc-slate text-sm">Loading…</p>
          ) : enquiries.length === 0 ? (
            <div
              data-testid="admin-empty-state"
              className="rounded-2xl border border-pawc-teal/15 bg-pawc-paper p-12 text-center text-pawc-slate text-sm"
            >
              No enquiries yet. New messages from the contact form will appear here.
            </div>
          ) : (
            <ul data-testid="admin-enquiries-list" className="space-y-5">
              {enquiries.map((q) => {
                const wa = waNumber(q.phone);
                const waText = encodeURIComponent(
                  `Hi ${q.name}, thank you for reaching out to Yielding: Flow and Form. This is Clive Benhura responding to your website enquiry.`
                );
                return (
                  <li
                    key={q.id}
                    data-testid={`admin-enquiry-${q.id}`}
                    className={`rounded-2xl border border-pawc-teal/15 bg-white p-7 transition-opacity duration-300 ${
                      q.answered ? "opacity-60" : ""
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                      <p className="font-serif text-xl text-pawc-deep flex items-center gap-3">
                        {q.name}
                        {q.answered && (
                          <span
                            data-testid={`answered-badge-${q.id}`}
                            className="text-[10px] tracking-[0.2em] uppercase font-sans px-3 py-1 rounded-full bg-pawc-teal/10 text-pawc-teal"
                          >
                            Answered
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-pawc-slate/70">
                        {new Date(q.created_at).toLocaleString("en-ZA", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </p>
                    </div>
                    <p className="text-sm text-pawc-slate mb-1">
                      <span className="text-pawc-teal">Email:</span> {q.email}
                      {q.phone ? (
                        <>
                          {"  ·  "}
                          <span className="text-pawc-teal">Phone:</span> {q.phone}
                        </>
                      ) : null}
                    </p>
                    <p className="text-sm text-pawc-slate mb-1">
                      <span className="text-pawc-teal">Regarding:</span> {q.service}
                    </p>
                    {q.preferred_day && q.preferred_time && (
                      <p className="text-sm text-pawc-slate mb-1">
                        <span className="text-pawc-teal">Preferred session:</span>{" "}
                        {q.preferred_day} at {q.preferred_time}
                        {q.preferred_location ? ` · ${q.preferred_location}` : ""}
                        {q.session_type ? ` · ${q.session_type}` : ""}
                        {q.session_length ? ` · ${q.session_length}` : ""}
                      </p>
                    )}
                    <p className="text-sm text-pawc-ink bg-pawc-paper rounded-xl p-4 whitespace-pre-wrap mt-3">
                      {q.message}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-5">
                      {wa ? (
                        <a
                          data-testid={`reply-whatsapp-${q.id}`}
                          href={`https://wa.me/${wa}?text=${waText}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-pawc-teal hover:bg-pawc-deep text-pawc-sand px-5 py-2.5 text-xs transition-colors duration-300"
                        >
                          <MessageCircle size={14} /> Reply by WhatsApp
                        </a>
                      ) : (
                        <a
                          data-testid={`reply-email-${q.id}`}
                          href={`mailto:${q.email}?subject=${encodeURIComponent("Re: Your enquiry to Yielding: Flow and Form")}`}
                          className="inline-flex items-center gap-2 rounded-full bg-pawc-teal hover:bg-pawc-deep text-pawc-sand px-5 py-2.5 text-xs transition-colors duration-300"
                        >
                          <Mail size={14} /> Reply by email
                        </a>
                      )}
                      <button
                        data-testid={`toggle-answered-${q.id}`}
                        onClick={() => toggleAnswered(q)}
                        className="inline-flex items-center gap-2 rounded-full border border-pawc-teal/30 text-pawc-teal px-5 py-2.5 text-xs hover:bg-pawc-mist transition-colors duration-300"
                      >
                        {q.answered ? (
                          <>
                            <Undo2 size={14} /> Mark as unread
                          </>
                        ) : (
                          <>
                            <Check size={14} /> Mark as answered
                          </>
                        )}
                      </button>
                      <button
                        data-testid={`delete-enquiry-${q.id}`}
                        onClick={() => deleteEnquiry(q)}
                        className="inline-flex items-center gap-2 rounded-full border border-red-300 text-red-700 px-5 py-2.5 text-xs hover:bg-red-50 transition-colors duration-300"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                      <button
                        data-testid={`draft-reply-${q.id}`}
                        onClick={() => draftReply(q)}
                        disabled={!!replyLoading[q.id]}
                        className="inline-flex items-center gap-2 rounded-full border border-pawc-terra/40 text-pawc-terra px-5 py-2.5 text-xs hover:bg-pawc-terra/10 disabled:opacity-50 transition-colors duration-300"
                      >
                        <Sparkles size={14} />
                        {replyLoading[q.id] ? "Drafting…" : "Draft reply with AI"}
                      </button>
                    </div>
                    {replyDrafts[q.id] !== undefined && (
                      <div
                        data-testid={`reply-draft-box-${q.id}`}
                        className="mt-4 rounded-xl border border-pawc-terra/25 bg-pawc-mist/50 p-5"
                      >
                        <p className="text-[10px] tracking-[0.2em] uppercase text-pawc-teal mb-2">
                          Claude's draft · edit before sending, never sent automatically
                        </p>
                        <p className="text-sm text-pawc-ink whitespace-pre-wrap leading-relaxed">
                          {replyDrafts[q.id]}
                          {replyLoading[q.id] && <span className="animate-pulse">▌</span>}
                        </p>
                        {!replyLoading[q.id] && replyDrafts[q.id] && (
                          <button
                            data-testid={`copy-draft-${q.id}`}
                            onClick={() => {
                              navigator.clipboard.writeText(replyDrafts[q.id]);
                              toast.success("Draft copied. Paste it into WhatsApp or email.");
                            }}
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-pawc-teal hover:bg-pawc-deep text-pawc-sand px-5 py-2 text-xs transition-colors duration-300"
                          >
                            <Copy size={13} /> Copy draft
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )
        )}

        {tab === "reflections" && !editing && (
          <div>
            <button
              data-testid="new-reflection-btn"
              onClick={() => openEditor(null)}
              className="inline-flex items-center gap-2 rounded-full bg-pawc-terra hover:bg-pawc-terraDark text-white px-6 py-3 text-sm mb-8 transition-colors duration-300"
            >
              <Plus size={16} /> New reflection
            </button>
            {posts === null ? (
              <p className="text-pawc-slate text-sm">Loading…</p>
            ) : posts.length === 0 ? (
              <div className="rounded-2xl border border-pawc-teal/15 bg-pawc-paper p-12 text-center text-pawc-slate text-sm">
                No reflections yet. Write your first one.
              </div>
            ) : (
              <ul data-testid="admin-reflections-list" className="space-y-4">
                {posts.map((p) => (
                  <li
                    key={p.id}
                    data-testid={`admin-post-${p.slug}`}
                    className="rounded-2xl border border-pawc-teal/15 bg-white p-6 flex flex-wrap items-center justify-between gap-4"
                  >
                    <div>
                      <p className="font-serif text-xl text-pawc-deep">{p.title}</p>
                      <p className="text-xs text-pawc-slate mt-1">
                        {p.tag} · {p.date} ·{" "}
                        <span className={p.published ? "text-pawc-teal" : "text-pawc-terra"}>
                          {p.published ? "Published" : "Draft (hidden)"}
                        </span>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid={`edit-post-${p.slug}`}
                        onClick={() => openEditor(p)}
                        className="inline-flex items-center gap-2 rounded-full border border-pawc-teal/30 text-pawc-teal px-4 py-2 text-xs hover:bg-pawc-mist transition-colors duration-300"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        data-testid={`delete-post-${p.slug}`}
                        onClick={() => deletePost(p)}
                        className="inline-flex items-center gap-2 rounded-full border border-red-300 text-red-700 px-4 py-2 text-xs hover:bg-red-50 transition-colors duration-300"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "reflections" && editing && (
          <form
            data-testid="reflection-editor"
            onSubmit={savePost}
            className="rounded-3xl bg-white border border-pawc-teal/15 p-8 md:p-10 space-y-5"
          >
            <h2 className="font-serif text-2xl text-pawc-deep mb-2">
              {editing === "new" ? "New reflection" : "Edit reflection"}
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <input
                data-testid="editor-title-input"
                required
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder="Title"
                className={`${inputCls} sm:col-span-2`}
                aria-label="Post title"
              />
              <input
                data-testid="editor-tag-input"
                value={draft.tag}
                onChange={(e) => setDraft((d) => ({ ...d, tag: e.target.value }))}
                placeholder="Tag (e.g. Presence)"
                className={inputCls}
                aria-label="Post tag"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                data-testid="editor-date-input"
                value={draft.date}
                onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                placeholder="Date label (e.g. September 2026)"
                className={inputCls}
                aria-label="Date label"
              />
              <input
                data-testid="editor-excerpt-input"
                value={draft.excerpt}
                onChange={(e) => setDraft((d) => ({ ...d, excerpt: e.target.value }))}
                placeholder="Short excerpt (shown on the card)"
                className={inputCls}
                aria-label="Excerpt"
              />
            </div>
            <div className="rounded-2xl border border-pawc-terra/25 bg-pawc-mist/40 p-5 space-y-3">
              <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-pawc-teal">
                <Sparkles size={14} /> Draft with Claude
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="ai-theme-input"
                  value={aiTheme}
                  onChange={(e) => setAiTheme(e.target.value)}
                  placeholder="A theme to write from, e.g. resting without guilt"
                  className={inputCls}
                  aria-label="Essay theme for AI draft"
                />
                <button
                  data-testid="ai-essay-btn"
                  type="button"
                  onClick={draftEssay}
                  disabled={aiBusy}
                  className="shrink-0 rounded-full bg-pawc-teal hover:bg-pawc-deep disabled:opacity-60 text-pawc-sand px-6 py-3 text-sm transition-colors duration-300"
                >
                  {aiBusy ? "Claude is writing…" : "Write draft"}
                </button>
              </div>
              <p className="text-[11px] text-pawc-slate/70">
                Claude Sonnet 4.6 writes a first draft in your voice, straight into the body below.
                It replaces the current body text. Edit freely before saving.
              </p>
            </div>
            <textarea
              data-testid="editor-body-input"
              required
              rows={12}
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              placeholder={"Write the reflection here.\n\nSeparate paragraphs with a blank line."}
              className={`${inputCls} resize-y leading-relaxed`}
              aria-label="Post body"
            />
            <input
              data-testid="editor-closing-input"
              value={draft.closing}
              onChange={(e) => setDraft((d) => ({ ...d, closing: e.target.value }))}
              placeholder="Closing line (optional, shown as a pull quote)"
              className={inputCls}
              aria-label="Closing line"
            />
            <label className="flex items-center gap-3 text-sm text-pawc-slate">
              <input
                data-testid="editor-published-checkbox"
                type="checkbox"
                checked={draft.published}
                onChange={(e) => setDraft((d) => ({ ...d, published: e.target.checked }))}
                className="w-4 h-4 accent-[#0E5F73]"
              />
              Published (visible on the website)
            </label>
            <div className="flex gap-3 pt-2">
              <button
                data-testid="editor-save-btn"
                type="submit"
                disabled={saving}
                className="rounded-full bg-pawc-terra hover:bg-pawc-terraDark disabled:opacity-60 text-white px-8 py-3 text-sm transition-colors duration-300"
              >
                {saving ? "Saving…" : "Save reflection"}
              </button>
              <button
                data-testid="editor-cancel-btn"
                type="button"
                onClick={() => { setEditing(null); setDraft(EMPTY_POST); }}
                className="rounded-full border border-pawc-teal/30 text-pawc-teal px-8 py-3 text-sm hover:bg-pawc-mist transition-colors duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
